import React from 'react';
import { Check, HelpCircle } from 'lucide-react';
import { assessments } from '../data/assessments';

export default function ComparisonMatrix() {
  const features = [
    {
      name: 'Risk Identification',
      description: 'Identifies and analyzes potential privacy risks in your data processing activities'
    },
    {
      name: 'Compliance Analysis',
      description: 'Evaluates compliance with applicable privacy laws and regulations'
    },
    {
      name: 'Impact Assessment',
      description: 'Assesses the potential impact of data processing on individual privacy rights'
    },
    {
      name: 'Mitigation Strategies',
      description: 'Provides actionable recommendations to reduce identified privacy risks'
    },
    {
      name: 'International Transfer',
      description: 'Evaluates compliance requirements for cross-border data transfers'
    },
    {
      name: 'Documentation Support',
      description: 'Includes detailed documentation to support compliance requirements'
    },
    {
      name: 'Executive Summary',
      description: 'Provides a high-level overview of findings and recommendations'
    },
    {
      name: 'Remediation Plan',
      description: 'Detailed action plan to address identified privacy gaps and risks'
    }
  ];

  const hasFeature = (assessmentId: string, featureName: string) => {
    // Simplified feature check logic
    const featureMatrix: Record<string, string[]> = {
      pa: ['Risk Identification', 'Executive Summary'],
      pia: ['Risk Identification', 'Impact Assessment', 'Mitigation Strategies', 'Executive Summary', 'Documentation Support'],
      dpia: features.map(f => f.name),
      pga: ['Risk Identification', 'Compliance Analysis', 'Documentation Support', 'Remediation Plan'],
      lia: ['Impact Assessment', 'Documentation Support', 'Executive Summary'],
      tra: ['Risk Identification', 'International Transfer', 'Mitigation Strategies', 'Documentation Support'],
      tia: ['Impact Assessment', 'International Transfer', 'Mitigation Strategies', 'Documentation Support', 'Remediation Plan']
    };

    return featureMatrix[assessmentId]?.includes(featureName) ?? false;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="text-center mb-12">
        
          <h2 className="text-2xl font-bold mb-4 relative font-montserrat">
            Assessment Comparison
            {/* Decorative underline */}
            <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-brand-primary to-transparent opacity-75" />
          </h2>
        
        <p className="text-lg text-gray-600 mt-6 max-w-2xl mx-auto">
          Compare our assessment offerings to find the right solution for your needs
        </p>
      </div>

      <div className="relative">
        {/* Background decorative elements */}
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-50 via-transparent to-emerald-50 opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-50/30 to-transparent" />

        <div className="relative overflow-x-auto">
          <div className="inline-block min-w-full align-middle">
            <div className="overflow-hidden shadow-lg rounded-xl bg-white border border-emerald-100">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gradient-to-r from-emerald-50 to-teal-50">
                    <th scope="col" className="py-4 pl-6 pr-3 text-left text-sm font-semibold text-gray-900">
                      Feature
                    </th>
                    {assessments.map((assessment) => (
                      <th key={assessment.id} scope="col" className="px-4 py-4 text-center">
                        <div className="flex flex-col items-center">
                          <span className="text-sm font-semibold text-gray-900">{assessment.shortName}</span>
                          <span className="text-xs text-gray-500 mt-1">${assessment.price}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {features.map((feature, featureIdx) => (
                    <tr 
                      key={feature.name} 
                      className={`
                        hover:bg-emerald-50 transition-colors group
                        ${featureIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                      `}
                    >
                      <td className="py-4 pl-6 pr-3 text-sm font-medium text-gray-900">
                        <div className="flex items-center">
                          <span>{feature.name}</span>
                          <div className="relative ml-2">
                            <HelpCircle className="w-4 h-4 text-gray-400" />
                            <div className="absolute left-0 bottom-full mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                              {feature.description}
                              <div className="absolute left-4 bottom-0 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900"></div>
                            </div>
                          </div>
                        </div>
                      </td>
                      {assessments.map((assessment) => (
                        <td key={assessment.id} className="px-4 py-4 text-center">
                          {hasFeature(assessment.id, feature.name) ? (
                            <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100 text-brand-primary">
                              <Check className="w-5 h-5" />
                            </div>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}